import { DEFAULT_EVENT_DURATION_MINUTES } from '@sixam/shared/event-flow-constants';

export type EventEditorSheetId =
  | 'schedule'
  | 'location'
  | 'route'
  | 'registration'
  | 'questions'
  | 'media'
  | 'links'
  | 'checkin'
  | 'visibility'
  | 'attendees'
  | 'payments'
  | 'notifications'
  | 'cancel';

export type EventEditorSaveIntent = 'draft' | 'published';

export const MAX_EVENT_TITLE_CHARACTERS = 120;
export const MAX_EVENT_DESCRIPTION_CHARACTERS = 1200;

export type EventEditorTextValidation =
  | Readonly<{ error: string; field: 'description' | 'title'; valid: false }>
  | Readonly<{ description: string; title: string; valid: true }>;

export function validateEventEditorText(
  title: string,
  description: string,
  descriptionRequired = true
): EventEditorTextValidation {
  const normalizedTitle = title.trim();
  const normalizedDescription = description.trim();

  if (!normalizedTitle) {
    return { error: 'Title is required.', field: 'title', valid: false };
  }
  if (normalizedTitle.length > MAX_EVENT_TITLE_CHARACTERS) {
    return {
      error: `Title must be ${MAX_EVENT_TITLE_CHARACTERS} characters or fewer.`,
      field: 'title',
      valid: false,
    };
  }
  if (descriptionRequired && !normalizedDescription) {
    return { error: 'Description is required.', field: 'description', valid: false };
  }
  if (descriptionRequired && normalizedDescription.length > MAX_EVENT_DESCRIPTION_CHARACTERS) {
    return {
      error: `Description must be ${MAX_EVENT_DESCRIPTION_CHARACTERS} characters or fewer.`,
      field: 'description',
      valid: false,
    };
  }

  return {
    description: normalizedDescription,
    title: normalizedTitle,
    valid: true,
  };
}

export type ClubPaymentDetails = Readonly<{
  thaiQrStoragePath: string;
  transferDetails: string;
}>;

export function numericInputValue(value: string): string {
  return value.replace(/\D/g, '');
}

export function paidEventDetailsError(
  priceThb: number,
  details: ClubPaymentDetails
): string | null {
  if (priceThb <= 0) return null;
  if (!details.thaiQrStoragePath.trim()) return 'Upload the club Thai payment QR code.';
  if (!details.transferDetails.trim()) return 'Add the club bank transfer details.';
  return null;
}

const HOUR_MS = 60 * 60 * 1000;

/** Returns the next future whole hour, never a time in the past. */
export function nextWholeHour(now: Date): Date {
  const next = new Date(now);
  next.setMinutes(0, 0, 0);
  next.setHours(next.getHours() + 1);
  return next;
}

export function createEventScheduleDefaults(now: Date): Readonly<{
  startsAt: Date;
  endsAt: Date;
}> {
  const startsAt = nextWholeHour(now);
  return {
    startsAt,
    endsAt: new Date(startsAt.getTime() + DEFAULT_EVENT_DURATION_MINUTES * 60 * 1000),
  };
}

/** Replaces the calendar date while preserving the exact existing time. */
export function replaceCalendarDate(value: Date, calendarDate: Date): Date {
  const next = new Date(value);
  next.setFullYear(calendarDate.getFullYear(), calendarDate.getMonth(), calendarDate.getDate());
  return next;
}

/** Intentionally moves a timestamp to one of the editor's whole-hour choices. */
export function replaceWithWholeHour(value: Date, hour: number): Date {
  const next = new Date(value);
  next.setHours(hour, 0, 0, 0);
  return next;
}

export function eventScheduleError(startsAt: Date, endsAt: Date | null): string | null {
  if (!endsAt || Number.isNaN(endsAt.getTime())) {
    return 'End date and time are required.';
  }
  if (Number.isNaN(startsAt.getTime())) {
    return 'Start date and time are required.';
  }
  if (endsAt.getTime() <= startsAt.getTime()) {
    return 'End must be after start.';
  }
  return null;
}

export function ensureValidEnd(startsAt: Date, currentEnd: Date): Date {
  return currentEnd.getTime() > startsAt.getTime()
    ? currentEnd
    : new Date(startsAt.getTime() + DEFAULT_EVENT_DURATION_MINUTES * 60 * 1000);
}

function dateFormatter(locale?: string, timeZone?: string) {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  });
}

function timeFormatter(locale?: string, timeZone?: string) {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
}

export function formatEditorDate(value: Date, locale?: string, timeZone?: string): string {
  return dateFormatter(locale, timeZone).format(value);
}

export function formatEditorTime(value: Date, locale?: string, timeZone?: string): string {
  return timeFormatter(locale, timeZone).format(value);
}

export function formatEditorDateTime(value: Date, locale?: string, timeZone?: string): string {
  return `${formatEditorDate(value, locale, timeZone)} · ${formatEditorTime(
    value,
    locale,
    timeZone
  )}`;
}

export function formatEditorSchedule(
  startsAt: Date,
  endsAt: Date,
  locale?: string,
  timeZone?: string
): string {
  return `${formatEditorDateTime(startsAt, locale, timeZone)} – ${formatEditorDateTime(
    endsAt,
    locale,
    timeZone
  )}`;
}

export function editorSheetForValidationField(field: string): EventEditorSheetId | null {
  if (field === 'startsAt' || field === 'endsAt') return 'schedule';
  if (field === 'place') return 'location';
  if (
    field === 'capacity' ||
    field === 'registrationPrice' ||
    field === 'paymentMethods' ||
    field === 'thaiQr' ||
    field === 'transferDetails'
  ) {
    return 'registration';
  }
  if (field === 'registrationForm') return 'questions';
  if (field === 'media') return 'media';
  if (field === 'eventLinks') return 'links';
  return null;
}

export function statusForCreateIntent(intent: EventEditorSaveIntent): EventEditorSaveIntent {
  return intent;
}

/** Every successful operator edit gets the attendee-notification decision. */
export function shouldPromptForEventUpdate(isEditing: boolean, isOperator: boolean): boolean {
  return isEditing && isOperator;
}

export function withSelectedCommunity<T extends object>(
  draft: T,
  tenantId: string
): T & Readonly<{ tenantId: string }> {
  return { ...draft, tenantId };
}

export function hourOptions(): readonly Readonly<{ label: string; value: number }>[] {
  return Array.from({ length: 24 }, (_, value) => ({
    label: `${String(value).padStart(2, '0')}:00`,
    value,
  }));
}

export function timestampUsesWholeHour(value: Date): boolean {
  return value.getMinutes() === 0 && value.getSeconds() === 0 && value.getMilliseconds() === 0;
}

export const ONE_HOUR_MS = HOUR_MS;
